import type { ReactNode } from "react";
import type { OtsToolGroup } from "../components/OtsToolsMenu";
import {
  activityActionIcon,
  activityActionSection,
  activityMenuSectionIcon,
  DRIVER_ACTIVITY_MENU_SECTIONS,
} from "./driver-activity-action-registry";
import type { ActivityEntry } from "./driver-activity-view-model";

/** What a tools-menu action needs to know; the workspace's `DriverActivityAction` satisfies it. */
export type ActivityToolAction = {
  id: string;
  label: string;
  scope: "single" | "any" | "optional";
  group?: "primary" | "tools" | "create";
  /** Section of the tools menu; WaterGo's sections are in `DRIVER_ACTIVITY_TOOL_GROUPS`. */
  toolGroup?: string;
  /** Shown beside the label; the menu picks one from the label otherwise. */
  icon?: ReactNode;
  /** Whether the action applies to these tickets, e.g. a delivery comment needs a delivery. */
  isEnabled?: (targets: ActivityEntry[]) => boolean;
  /** Whether to offer it at all for these tickets; hidden when not. */
  isVisible?: (targets: ActivityEntry[]) => boolean;
};

/** An action is offered for these tickets: anything that does not say otherwise is. */
export const isActivityActionVisible = (action: ActivityToolAction, targets: ActivityEntry[]): boolean =>
  action.isVisible?.(targets) ?? true;

/** An action can run on these tickets: the right count for its scope, and whatever it checks itself. */
export const isActivityActionEnabled = (action: ActivityToolAction, targets: ActivityEntry[]): boolean =>
  (action.scope !== "single" || targets.length === 1)
  && (targets.length > 0 || action.scope === "optional")
  && (action.isEnabled?.(targets) ?? true);

/** The menu's sections in order; see `DRIVER_ACTIVITY_MENU_SECTIONS`. */
export const DRIVER_ACTIVITY_TOOL_GROUPS = DRIVER_ACTIVITY_MENU_SECTIONS;

const groupId = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const sectionRank = (label: string) => {
  const index = (DRIVER_ACTIVITY_MENU_SECTIONS as readonly string[]).indexOf(label);
  // A host's own sections go after the workspace's, but Delete stays last.
  return index >= 0 ? index : DRIVER_ACTIVITY_MENU_SECTIONS.indexOf("Delete") - 0.5;
};

/**
 * Sorts actions into tools-menu sections for the tickets they would act on. An action that does not apply
 * (a single-ticket action on a selection, a delivery comment on a pickup) stays listed but disabled, so the
 * menu keeps its shape.
 */
export const buildActivityToolGroups = (actions: ActivityToolAction[], targets: ActivityEntry[]): OtsToolGroup[] => {
  const sections = new Map<string, OtsToolGroup>();
  for (const action of actions) {
    const label = activityActionSection(action);
    const section = sections.get(label) ?? { id: groupId(label), label, icon: activityMenuSectionIcon(label), actions: [] };
    section.actions.push({
      id: action.id,
      label: action.label,
      icon: activityActionIcon(action),
      disabled: !isActivityActionEnabled(action, targets),
      hidden: !isActivityActionVisible(action, targets),
    });
    sections.set(label, section);
  }
  // Map keeps first-seen order, so a host's sections stay in its order among themselves.
  return [...sections.values()].sort((left, right) => sectionRank(left.label) - sectionRank(right.label));
};
